import { EventContextProvider, useAnalytics } from "@codat/analytics";
import { Modal, useToast } from "@codat/orchard-ui";
import { usePostAboutCompany } from "api/portal";
import { useUpdateProductStatus } from "api/public";
import { Product } from "api/schema/productsSchema";
import { useProducts } from "contexts/ProductsContext";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { FC, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import formatUrl from "utils/formatUrl";

import { paths } from "components/App/paths";
import LoadingPantoModal from "components/LoadingPantoModal";
import { ProductSelect } from "components/ProductSelect";
import api from "config/urls/api";

import { CompanyNameModal, HaveExistingIntegrationsModal } from "./";
import { IntegrationsWithNoSetupModal } from "./IntegrationsWithNoSetupModal";
import styles from "./Onboarding.module.css";

export interface OnboardingProps {
  /** For testing purposes only. */
  _initialStep?: number;
}

export const Onboarding: FC<OnboardingProps> = (props) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(
    undefined
  );

  const [aboutCompany, setAboutCompany] = useState({
    countryCode: "",
    jobTitle: "",
    employeeRange: "",
  });

  const [step, setStep] = useState(props._initialStep ?? 1);
  const form = useForm({
    mode: "onChange",
    defaultValues: {
      countryCode: "",
      employeeRange: "",
      jobTitle: "",
      selectedProduct: "",
      hasAnIntegration: "no",
      migrateTokens: false,
      whatAreYouBuilding: "",
    },
  });

  const [, enableProduct] = useUpdateProductStatus({
    productId: selectedProduct?.productId ?? "null",
    status: "Enabled",
  });

  const { addToast } = useToast();
  const history = useHistory<{ prevPath: string }>();
  const { refetch: refetchProducts, products } = useProducts();
  const { userState } = useUserState();
  const { setUserProperties } = useAnalytics();

  const [countryCode, employeeRange, jobTitle] = form.watch([
    "countryCode",
    "employeeRange",
    "jobTitle",
    "selectedProduct",
    "hasAnIntegration",
    "migrateTokens",
    "whatAreYouBuilding",
  ]);

  const [{ loading: postAboutCompanyLoading }, sendAboutCompany] =
    usePostAboutCompany(aboutCompany);

  const isEnabling = step === 5;
  const redirectToHomePage = () => {
    const pathname = (() => {
      if (
        history.location?.state?.prevPath &&
        history.location.state.prevPath !== paths.ONBOARDING
      ) {
        return history.location?.state?.prevPath;
      }
      return "/";
    })();
    return history.replace({
      pathname,
    });
  };
  const incrementStep = () => {
    setStep((currentStep) => currentStep + 1);
  };

  useEffect(() => {
    if (countryCode && jobTitle && employeeRange) {
      setAboutCompany({ countryCode, jobTitle, employeeRange });
    }
  }, [countryCode, jobTitle, employeeRange]);

  const onCompanyNameModalContinue = async () => {
    await sendAboutCompany().catch(() => undefined);
    setUserProperties({
      department: aboutCompany.jobTitle,
      countryCode: aboutCompany.countryCode,
    });
    incrementStep();
  };

  const onProductSelect = (product: Product) => {
    enableProduct({
      url: formatUrl(api.products.one, { productId: product.productId }),
    })
      .then(() => {
        refetchProducts().then(() => {
          setSelectedProduct(product);
          incrementStep();
        });
      })
      .catch(() => {
        addToast({
          message: `Failed to enable ${product.name}`,
          variant: "error",
        });
      });
  };

  const pantoItems = useMemo(
    () =>
      [
        `Creating user ${userState?.userName}`,
        `Setting up organization`,
        selectedProduct ? `Enabling ${selectedProduct.name}` : "",
      ].filter(Boolean),
    [userState, selectedProduct]
  );

  const onboardingJourneyElement = (() => {
    switch (step) {
      case 1:
        return (
          <EventContextProvider
            name="Company Name Modal"
            key="companyNameModal"
          >
            <CompanyNameModal
              companyName={userState?.clientName}
              onContinue={onCompanyNameModalContinue}
              loading={postAboutCompanyLoading}
            />
          </EventContextProvider>
        );
      case 2: {
        return (
          <ProductSelect
            onProductSelect={onProductSelect}
            products={products}
          />
        );
      }
      case 3:
        return (
          <EventContextProvider
            name="Have a Solution Modal"
            key="solutionModal"
          >
            <HaveExistingIntegrationsModal
              onContinue={incrementStep}
              onSkip={incrementStep}
            />
          </EventContextProvider>
        );
      case 4:
        return (
          <EventContextProvider
            name="Integrations With No Setup Modal"
            key="integrationsWithNoSetupModal"
          >
            <IntegrationsWithNoSetupModal onSkip={incrementStep} />
          </EventContextProvider>
        );
    }
  })();

  return (
    <>
      <Modal
        size={step === 2 ? "large" : "small"}
        className={styles.modal}
        onClose={() => {
          // Disable outside click to close
        }}
      >
        {isEnabling ? (
          <LoadingPantoModal
            title="Creating your environment"
            items={pantoItems}
            onComplete={redirectToHomePage}
          />
        ) : (
          <FormProvider {...form}>{onboardingJourneyElement}</FormProvider>
        )}
      </Modal>
    </>
  );
};
